import { useQuery } from 'react-query';
import { BASE_API } from 'src/shared/services/axios';
import { PartnerConfig, PartnerConfigResponse } from 'src/shared/types';

const STORAGE_PREFIX = 'partner-config';
const FALLBACK_PARTNER_CONFIG: PartnerConfig = {
  id: '',
  name: 'SMRTComp',
  domain: '',
  logo_url: null,
  theme: {
    title: 'SMRTComp',
    fonts: {
      base: 'Roboto',
    },
    favicon_url: '/favicon.png',
    google_font: null,
  },
  palette: null,
};

function getStorageKey(hostname: string) {
  return `${STORAGE_PREFIX}:${hostname}`;
}

function readCachedPartnerConfig(hostname: string): PartnerConfig | null | undefined {
  try {
    const cached = localStorage.getItem(getStorageKey(hostname));
    if (!cached) return undefined;
    return JSON.parse(cached) as PartnerConfig | null;
  } catch {
    return undefined;
  }
}

function writeCachedPartnerConfig(hostname: string, partner: PartnerConfig | null) {
  try {
    localStorage.setItem(getStorageKey(hostname), JSON.stringify(partner));
  } catch {
    // Ignore storage errors (private mode, quota, etc).
  }
}

export function usePartnerConfig() {
  const hostname = window.location.host;
  const cachedPartner = readCachedPartnerConfig(hostname);

  const {
    data,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['partner-config', hostname],
    queryFn: async () => {
      try {
        const { data } = await BASE_API.get<PartnerConfigResponse>(
          '/tenants/partner-config',
        );
        const partner = data?.partner ?? null;
        writeCachedPartnerConfig(hostname, partner);
        return partner;
      } catch {
        if (cachedPartner !== undefined) return cachedPartner;
        return null;
      }
    },
    initialData: cachedPartner,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: 2,
  });

  const partnerConfig =
    data ?? (isLoading ? null : FALLBACK_PARTNER_CONFIG);

  return {
    partnerConfig: partnerConfig ?? null,
    isLoading,
    isFallback: !isLoading && data === null,
    refetch,
  };
}
